// src/content/articles.mjs
// Article content shared by the React app (ArticlesIndex / ArticlePage) and the
// build-time prerenderer (scripts/prerender.mjs). Plain data, no JSX, so Node
// can import it directly during the build.
//
// To publish a new article, add an entry here and run `npm run build`.
// Section paragraphs are plain text (escaped when prerendered) — no HTML.

import generatedArticles from './generated-articles.mjs';

export const SITE_URL = 'https://healthsa.pages.dev';
export const SITE_NAME = 'HealthSA';
export const SITE_LOGO = `${SITE_URL}/logo512.png`;
// Shared social preview image. logo512 is square, so pages use twitter:card "summary".
export const OG_IMAGE = SITE_LOGO;

const SEED_ARTICLES = [
  {
    slug: 'how-medicines-are-approved-worldwide',
    title: 'How Medicines Are Approved Around the World: SAHPRA, FDA, EMA and WHO',
    description:
      'A plain-language guide to how new medicines are evaluated and registered by major regulators — the FDA, EMA, MHRA, SAHPRA and the WHO prequalification programme.',
    datePublished: '2026-07-21',
    dateModified: '2026-07-21',
    tags: ['regulation', 'SAHPRA', 'FDA', 'EMA', 'WHO'],
    sections: [
      {
        heading: null,
        paragraphs: [
          'Before any medicine reaches a pharmacy shelf, it passes through a regulatory approval process designed to establish three things: that it is safe for its intended use, that it actually works, and that it is manufactured to a consistent quality. Every country runs some version of this process, but the systems differ in scale, speed and approach.',
          'This article gives a plain-language overview of how the major regulators evaluate medicines, and how South Africa’s SAHPRA fits into the global picture.',
        ],
      },
      {
        heading: 'The big three: FDA, EMA and MHRA',
        paragraphs: [
          'The United States Food and Drug Administration (FDA) is the world’s most influential medicines regulator. Manufacturers submit a New Drug Application (NDA) or, for biologics, a Biologics License Application (BLA), containing the full clinical trial record. FDA review typically takes 10 to 12 months under standard review, or around 6 months under priority review.',
          'The European Medicines Agency (EMA) coordinates a centralised procedure for the European Union: a single scientific assessment by its Committee for Medicinal Products for Human Use (CHMP) leads to a marketing authorisation valid across all EU member states.',
          'The United Kingdom’s MHRA became a fully independent regulator again after Brexit and now runs its own national approvals, alongside international reliance routes that let it fast-track products already approved by trusted peer agencies.',
        ],
      },
      {
        heading: 'How SAHPRA registers medicines in South Africa',
        paragraphs: [
          'The South African Health Products Regulatory Authority (SAHPRA) is responsible for registering every medicine sold in South Africa under the Medicines and Related Substances Act. A product registered by SAHPRA receives a registration number and appears in the public registration database.',
          'Like many mid-sized regulators, SAHPRA makes extensive use of regulatory reliance: where a product has already been approved by a stringent regulatory authority such as the FDA or EMA, SAHPRA can lean on that assessment rather than repeating the entire review from scratch. This shortens timelines without lowering the evidence bar.',
          'SAHPRA also schedules medicines (Schedule 0 through Schedule 8), which determines whether a product is available off the shelf, from a pharmacist, or on prescription only.',
        ],
      },
      {
        heading: 'The WHO prequalification programme',
        paragraphs: [
          'For many low- and middle-income countries, the World Health Organization’s prequalification (PQ) programme acts as a trusted quality benchmark. WHO-prequalified medicines, vaccines and diagnostics have been assessed for quality, safety and efficacy, and international procurement agencies such as the Global Fund and UNICEF generally require prequalification before purchasing.',
          'Prequalification matters enormously for products like antiretrovirals, TB treatments and antimalarials — categories that dominate the public-health burden in Southern Africa.',
        ],
      },
      {
        heading: 'Why the same medicine can have different approval status by country',
        paragraphs: [
          'Approval is national (or regional). A medicine approved by the FDA is not automatically legal to sell in South Africa, and vice versa. Differences in approved indications, dosage forms and even brand names between countries are common and normal.',
          'That is why checking a local register matters: in South Africa, SAHPRA’s database is the authoritative record of what is actually registered, and the Medicine Price Registry records the regulated Single Exit Price for each registered product. You can search both from the HealthSA search page.',
        ],
      },
    ],
    references: [
      { title: 'SAHPRA — South African Health Products Regulatory Authority', url: 'https://www.sahpra.org.za/' },
      { title: 'U.S. FDA — Development & Approval Process (Drugs)', url: 'https://www.fda.gov/drugs/development-approval-process-drugs' },
      { title: 'EMA — Authorisation of medicines', url: 'https://www.ema.europa.eu/en/about-us/what-we-do/authorisation-medicines' },
      { title: 'WHO Prequalification of Medical Products', url: 'https://extranet.who.int/prequal/' },
    ],
  },
  {
    slug: 'generic-medicines-explained',
    title: 'Generic Medicines Explained: Bioequivalence, INNs and Why Generics Cost Less',
    description:
      'What a generic medicine actually is, how bioequivalence testing works, why generics are cheaper than originators, and how generic substitution works in South Africa.',
    datePublished: '2026-07-21',
    dateModified: '2026-07-21',
    tags: ['generics', 'bioequivalence', 'pricing'],
    sections: [
      {
        heading: null,
        paragraphs: [
          'A generic medicine contains the same active ingredient, in the same strength and dosage form, as an originator (brand-name) product whose patent has expired. Generics account for the large majority of prescriptions dispensed worldwide — and understanding how they are approved explains why they can be trusted at a fraction of the originator’s price.',
        ],
      },
      {
        heading: 'The active ingredient and the INN',
        paragraphs: [
          'Every active ingredient has an International Nonproprietary Name (INN) assigned by the World Health Organization — for example, paracetamol, amoxicillin or atorvastatin. Brand names differ by company and country, but the INN is the same everywhere, which is why searching by active ingredient is usually the most reliable way to compare products.',
        ],
      },
      {
        heading: 'What bioequivalence means',
        paragraphs: [
          'A generic does not repeat the originator’s full clinical trial programme. Instead, the manufacturer must demonstrate bioequivalence: that the generic delivers the same active ingredient into the bloodstream at the same rate and extent as the reference product, within tight statistical limits (typically 80–125% for the key pharmacokinetic measures).',
          'Because the active ingredient’s safety and efficacy were already established by the originator’s trials, bioequivalence plus proof of manufacturing quality is scientifically sufficient. This is the position of essentially every major regulator, including the FDA, EMA and SAHPRA.',
        ],
      },
      {
        heading: 'Why generics are cheaper',
        paragraphs: [
          'Generic manufacturers do not carry the cost of discovery research or the original clinical trials, and competition between multiple generic makers drives prices down further. When several generics of the same molecule are on the market, prices commonly fall by 80% or more relative to the pre-expiry brand price.',
        ],
      },
      {
        heading: 'Generic substitution in South Africa',
        paragraphs: [
          'South African law actively encourages generic use: under the Medicines and Related Substances Act, pharmacists are generally required to inform patients of the availability of a cheaper generic equivalent and to offer substitution, unless the prescriber has expressly forbidden it or the product appears on a non-substitutable list.',
          'Because every registered product in South Africa has a regulated Single Exit Price, comparing the SEP of an originator and its generics is straightforward — the Medicine Price Registry publishes them all, and you can compare prices for any molecule using the HealthSA search page.',
        ],
      },
    ],
    references: [
      { title: 'WHO — International Nonproprietary Names (INN)', url: 'https://www.who.int/teams/health-product-and-policy-standards/inn' },
      { title: 'U.S. FDA — Generic Drug Facts', url: 'https://www.fda.gov/drugs/generic-drugs/generic-drug-facts' },
      { title: 'Medicine Price Registry (South Africa)', url: 'https://medicineprices.org.za/' },
    ],
  },
  {
    slug: 'single-exit-price-explained',
    title: 'What Is the Single Exit Price (SEP)? South Africa’s Medicine Pricing System',
    description:
      'How South Africa’s Single Exit Price system works: one regulated price per medicine, the annual price adjustment, dispensing fees, and how to look up any SEP.',
    datePublished: '2026-07-21',
    dateModified: '2026-07-21',
    tags: ['pricing', 'SEP', 'South Africa'],
    sections: [
      {
        heading: null,
        paragraphs: [
          'South Africa regulates private-sector medicine prices through a mechanism that is unusual internationally: the Single Exit Price, or SEP. Introduced in 2004 under the Medicines and Related Substances Act, the SEP is the one and only price at which a manufacturer may sell a medicine to anyone other than the state.',
        ],
      },
      {
        heading: 'One price for everyone',
        paragraphs: [
          'Before the SEP, discounts, rebates and bonuses to different buyers made real medicine prices opaque. The SEP abolished that: every pharmacy, wholesaler and hospital buys a given product at the same published price. The only additions allowed downstream are a regulated logistics fee and a regulated dispensing fee charged by the pharmacy.',
          'The SEP applies to the private sector. The state procures medicines separately through national tenders, usually at substantially lower prices.',
        ],
      },
      {
        heading: 'How prices change',
        paragraphs: [
          'Manufacturers may not raise the SEP at will. Once a year, the Minister of Health publishes a maximum permitted annual price adjustment, informed by inflation, exchange rates and international pricing. Manufacturers can increase prices up to that percentage — or choose not to. Price decreases are allowed at any time.',
        ],
      },
      {
        heading: 'Looking up a Single Exit Price',
        paragraphs: [
          'The Department of Health publishes the full SEP database, and the community-run Medicine Price Registry (medicineprices.org.za) makes it searchable. Each entry lists the product, pack size, NAPPI code and current SEP.',
          'HealthSA’s search combines this pricing data with SAHPRA registration status, so a single search shows both what a medicine costs and whether — and how — it is registered. Because the SEP is the same everywhere, if a private-sector quote differs materially from the published SEP plus the regulated dispensing fee, it is worth querying.',
        ],
      },
    ],
    references: [
      { title: 'Medicine Price Registry (South Africa)', url: 'https://medicineprices.org.za/' },
      { title: 'National Department of Health — Medicine pricing regulations', url: 'https://www.health.gov.za/' },
    ],
  },
  {
    slug: 'what-is-a-nappi-code',
    title: 'What Is a NAPPI Code? South Africa’s Medicine Product Codes Explained',
    description:
      'NAPPI codes uniquely identify every medicine pack sold in South Africa. What the code means, who issues it, and how it compares to ATC codes and the US NDC system.',
    datePublished: '2026-07-21',
    dateModified: '2026-07-21',
    tags: ['NAPPI', 'coding', 'South Africa'],
    sections: [
      {
        heading: null,
        paragraphs: [
          'If you have ever looked at a South African pharmacy invoice or a medical-aid claim, you have seen a NAPPI code. The National Pharmaceutical Product Index (NAPPI) is South Africa’s universal product identifier for medicines and surgical items: a unique number for every product, strength, dosage form and pack size on the market.',
        ],
      },
      {
        heading: 'What the code identifies',
        paragraphs: [
          'A NAPPI code pins down a product far more precisely than a name. “Amoxicillin 500 mg capsules, pack of 15, Brand X” has a different NAPPI code from the same brand’s pack of 20, and from every competitor’s 500 mg capsule. This precision is what lets medical schemes adjudicate claims automatically and lets pricing databases like the Medicine Price Registry attach an exact Single Exit Price to an exact pack.',
          'NAPPI codes are issued and maintained by MediKredit, a private company, and are used across the entire South African healthcare claims ecosystem.',
        ],
      },
      {
        heading: 'How NAPPI compares to other coding systems',
        paragraphs: [
          'Internationally, several coding systems coexist and serve different purposes. The WHO’s ATC (Anatomical Therapeutic Chemical) classification groups active ingredients by therapeutic class — useful for research and comparing like with like, but it does not identify a specific pack. The United States uses the NDC (National Drug Code) — like NAPPI, a pack-level identifier — while many countries rely on GTIN barcodes for supply-chain traceability.',
          'In practice: ATC tells you what kind of medicine something is; NAPPI (or NDC) tells you exactly which product and pack it is.',
        ],
      },
      {
        heading: 'Searching by NAPPI code',
        paragraphs: [
          'Because a NAPPI code is unambiguous, it is often the fastest way to look up a product. The HealthSA search page accepts NAPPI codes directly and returns the matching product with its current Single Exit Price and SAHPRA registration details.',
        ],
      },
    ],
    references: [
      { title: 'MediKredit — NAPPI', url: 'https://www.medikredit.co.za/' },
      { title: 'WHO Collaborating Centre — ATC/DDD Index', url: 'https://atcddd.fhi.no/atc_ddd_index/' },
      { title: 'Medicine Price Registry (South Africa)', url: 'https://medicineprices.org.za/' },
    ],
  },
  {
    slug: 'who-essential-medicines-list',
    title: 'The WHO Essential Medicines List: What It Is and Why It Matters',
    description:
      'The WHO Model List of Essential Medicines shapes which medicines health systems prioritise worldwide. How the list works and how South Africa’s EML relates to it.',
    datePublished: '2026-07-21',
    dateModified: '2026-07-21',
    tags: ['WHO', 'essential medicines', 'policy'],
    sections: [
      {
        heading: null,
        paragraphs: [
          'Since 1977, the World Health Organization has maintained a Model List of Essential Medicines (EML) — the medicines considered most important for meeting a population’s priority health needs. The list is updated every two years by an expert committee and currently covers several hundred active ingredients, from basic analgesics to cancer treatments and antivirals.',
        ],
      },
      {
        heading: 'What “essential” means',
        paragraphs: [
          'Essential medicines are selected on the basis of disease prevalence, evidence of efficacy and safety, and comparative cost-effectiveness. The idea is not that other medicines are unnecessary, but that a functioning health system should ensure these particular medicines are always available, in adequate quantities, in the right dosage forms, at prices people and systems can afford.',
          'There is a companion list for children (the EMLc), reflecting the different formulations and doses paediatric care requires.',
        ],
      },
      {
        heading: 'How countries use the model list',
        paragraphs: [
          'The WHO list is a model, not a mandate. More than 150 countries derive their own national essential medicines lists from it, adapting for local disease burden and budgets. National lists then drive public-sector procurement, standard treatment guidelines and training.',
          'South Africa maintains its own Essential Medicines List with accompanying Standard Treatment Guidelines, split into primary healthcare, adult hospital and paediatric hospital levels. The national EML determines what the state buys on tender and what a public clinic is expected to stock.',
        ],
      },
      {
        heading: 'Why the list matters for access and pricing',
        paragraphs: [
          'Inclusion on the WHO list carries real weight: it strengthens the case for generic manufacture, guides donor and procurement-agency purchasing, and is frequently cited in pricing and licensing negotiations. The 2015 addition of new hepatitis C antivirals and successive expansions of the cancer section, for example, put sustained international pressure on the prices of those medicines.',
          'For South African readers, a practical takeaway: most medicines on the national EML have multiple registered suppliers and regulated Single Exit Prices, which you can compare using the HealthSA search page.',
        ],
      },
    ],
    references: [
      { title: 'WHO Model Lists of Essential Medicines', url: 'https://www.who.int/groups/expert-committee-on-selection-and-use-of-essential-medicines/essential-medicines-lists' },
      { title: 'South African National Essential Medicines List', url: 'https://www.health.gov.za/nhi-emlc/' },
    ],
  },
];

// Seed (hand-written) + generated articles, newest first.
export const ARTICLES = [...SEED_ARTICLES, ...generatedArticles].sort((a, b) =>
  b.datePublished.localeCompare(a.datePublished)
);

export function getArticle(slug) {
  return ARTICLES.find((a) => a.slug === slug) || null;
}

// Related articles for internal linking: ranked by number of shared tags,
// tie-broken by recency, falling back to the most recent other articles.
export function getRelatedArticles(slug, limit = 3) {
  const current = ARTICLES.find((a) => a.slug === slug);
  if (!current) return [];
  const curTags = new Set((current.tags || []).map((t) => t.toLowerCase()));
  return ARTICLES.filter((a) => a.slug !== slug)
    .map((a) => ({
      a,
      score: (a.tags || []).filter((t) => curTags.has(t.toLowerCase())).length,
    }))
    .sort((x, y) => y.score - x.score || y.a.datePublished.localeCompare(x.a.datePublished))
    .slice(0, limit)
    .map((x) => x.a);
}
